/** Alineado a `cl-microcentrum-cursos/adapters/roles.ts` → `determinarRoles`. */
export const COURSE_ROLE = {
    GUARDIAN: "guardian",
    PRESIDENT: "president",
    TREASURER: "treasurer",
    SECRETARY: "secretary",
    DIRECTOR: "director",
    VICEPRESIDENT: "vicepresident",
    DELEGATE1: "delegate1",
    DELEGATE2: "delegate2",
};
/** Roles que identifican a un miembro de la directiva del grupo. */
export const ROLES_MIEMBRO_DIRECTIVA = [
    COURSE_ROLE.PRESIDENT,
    COURSE_ROLE.TREASURER,
    COURSE_ROLE.SECRETARY,
    COURSE_ROLE.DIRECTOR,
    COURSE_ROLE.VICEPRESIDENT,
    COURSE_ROLE.DELEGATE1,
    COURSE_ROLE.DELEGATE2,
];
/**
 * Directiva CPA con menú/gestión completa.
 * Incluye director; excluye delegados (permiso mínimo aparte).
 */
export const ROLES_CPA_DIRECTIVA_GESTION = [
    COURSE_ROLE.PRESIDENT,
    COURSE_ROLE.TREASURER,
    COURSE_ROLE.SECRETARY,
    COURSE_ROLE.DIRECTOR,
    COURSE_ROLE.VICEPRESIDENT,
];
/** Delegados del CPA: acceso mínimo (p. ej. solo Mi cuenta). */
export const ROLES_CPA_DELEGADO = [
    COURSE_ROLE.DELEGATE1,
    COURSE_ROLE.DELEGATE2,
];
const normalizeEmail = (email) => email.trim().toLowerCase();
const sameEmail = (a, b) => {
    if (!a?.trim() || !b.trim())
        return false;
    return normalizeEmail(a) === normalizeEmail(b);
};
/**
 * Misma lógica que backend `determinarRoles` / `obtenerAccesoUsuario.rolesFromCourse`.
 */
export const determinarRolesEnCurso = (curso, userEmail) => {
    const email = normalizeEmail(userEmail);
    const isPresident = sameEmail(curso.president, email);
    const isSecretary = sameEmail(curso.secretary, email);
    const isTreasurerFormal = sameEmail(curso.treasurer, email);
    const isTreasurerByDelegation = (isPresident && curso.president_treasurer === true) ||
        (isSecretary && curso.secretary_treasurer === true);
    const isTreasurer = isTreasurerFormal || isTreasurerByDelegation;
    const roles = [];
    if (isPresident)
        roles.push(COURSE_ROLE.PRESIDENT);
    if (isSecretary)
        roles.push(COURSE_ROLE.SECRETARY);
    if (isTreasurer)
        roles.push(COURSE_ROLE.TREASURER);
    if (sameEmail(curso.director, email))
        roles.push(COURSE_ROLE.DIRECTOR);
    if (sameEmail(curso.vicepresident, email)) {
        roles.push(COURSE_ROLE.VICEPRESIDENT);
    }
    if (sameEmail(curso.delegate1, email))
        roles.push(COURSE_ROLE.DELEGATE1);
    if (sameEmail(curso.delegate2, email))
        roles.push(COURSE_ROLE.DELEGATE2);
    if (roles.length === 0) {
        roles.push(COURSE_ROLE.GUARDIAN);
    }
    return roles;
};
const tieneEmailsDirectiva = (curso) => Boolean(curso.president?.trim() ||
    curso.treasurer?.trim() ||
    curso.secretary?.trim() ||
    curso.director?.trim() ||
    curso.vicepresident?.trim() ||
    curso.delegate1?.trim() ||
    curso.delegate2?.trim());
/**
 * Roles efectivos para guards de ruta: prioriza recálculo con delegación si hay
 * emails en el curso; si no, confía en `roles[]` del login (getGuardianWithCourses).
 */
export const effectiveRolesForAccess = (curso, userEmail) => {
    if (!curso)
        return [];
    if (userEmail?.trim() && tieneEmailsDirectiva(curso)) {
        return determinarRolesEnCurso(curso, userEmail);
    }
    return [...(curso.roles ?? [])];
};
export const hasRoleInCurso = (curso, userEmail, allowed) => {
    const roles = effectiveRolesForAccess(curso, userEmail);
    return allowed.some((r) => roles.includes(r));
};
/** Tesorero formal o por delegación (`president_treasurer` / `secretary_treasurer`). */
export const canActAsTreasurer = (curso, userEmail) => hasRoleInCurso(curso, userEmail, [COURSE_ROLE.TREASURER]);
/**
 * Editar, resetear o eliminar un usuario del curso, y gestionar apoderado extra.
 * No requiere delegación de tesorería: basta ser presidente, secretario o tesorero.
 */
export const canManageCourseUsers = (curso, userEmail) => hasRoleInCurso(curso, userEmail, [
    COURSE_ROLE.PRESIDENT,
    COURSE_ROLE.SECRETARY,
    COURSE_ROLE.TREASURER,
]);
export const canActAsDirectivaGestion = (curso, userEmail) => hasRoleInCurso(curso, userEmail, [
    COURSE_ROLE.PRESIDENT,
    COURSE_ROLE.TREASURER,
    COURSE_ROLE.SECRETARY,
    COURSE_ROLE.VICEPRESIDENT,
]);
/** Roles que pueden administrar comisiones (curso o CPA). */
export const ROLES_COMISIONES_ADMIN = [
    COURSE_ROLE.PRESIDENT,
    COURSE_ROLE.TREASURER,
    COURSE_ROLE.SECRETARY,
    COURSE_ROLE.VICEPRESIDENT,
];
export const canActAsComisionesAdmin = (curso, userEmail) => hasRoleInCurso(curso, userEmail, ROLES_COMISIONES_ADMIN);
/** Modificar directiva — cualquier miembro de la directiva del grupo. */
export const canActAsMiembroDirectiva = (curso, userEmail) => hasRoleInCurso(curso, userEmail, ROLES_MIEMBRO_DIRECTIVA);
/** Modificar directiva / tipo tesorería — secretario excluido. */
export const canActAsDirectivaConfig = (curso, userEmail) => hasRoleInCurso(curso, userEmail, [
    COURSE_ROLE.PRESIDENT,
    COURSE_ROLE.TREASURER,
]);
export const isMiembroCurso = (curso) => Boolean(curso?.school_id && curso?.year && (curso.course_code || curso.course_id));
